// Core domain types shared between the API (Lambda) and the web SPA.
// Mirrors the schema design in PLAN.md §6. This is the Phase-1 subset;
// individual event payloads are added incrementally per the §23 build sequence.

import type { ScheduledGroup } from "./medication.js";
import type { AppMode } from "./modelConfig.js";
import type { TrainingGoal } from "./exerciseDosing.js";
import type { ChatDepth } from "./chatDepth.js";

/** All structured event types the app can record (PLAN.md §6.1, Batches 1–6). */
export const EVENT_TYPES = [
  "medication_taken",
  "as_needed_med",
  "patch_change",
  "vital_sign",
  "sleep",
  "symptom",
  "headache",
  "visit",
  "vaccination",
  "illness",
  "family_history",
  "food",
  "hydration",
  "stress_event",
  "exercise",
  "mood",
  "allergy",
  "bowel_movement",
  "environmental_exposure",
  "surgery_or_procedure",
  "hospitalization",
  "lab_result",
  "cycle_event",
  "report",
  "document",
  "injury",
  "pt_program",
  "pt_session",
  "clinical_note",
  "body_composition",
  "body_measurement",
  "gym_exercise",
  "gym_program",
  "treatment",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/** Body-site laterality, shared across event types that record an anatomical
 *  site (illness, surgery_or_procedure). Mirrors the symptom `side` value set. */
export const LATERALITY = ["left", "right", "bilateral", "midline", "n/a"] as const;
export type Laterality = (typeof LATERALITY)[number];

/** How a record entered the system (PLAN.md §6 envelope). */
export type EventSource =
  | "chat"
  | "manual"
  | "import_csv"
  | "import_pdf"
  | "import_image"
  | "apple_health_xml"
  | "apple_health_webhook"
  | "calendar";

/** Audit envelope present on every stored event (PLAN.md §6, D-1). */
export interface AuditEnvelope {
  recordedAt: string; // ISO-8601 server time
  source: EventSource;
  sourceRefs: string[]; // e.g. ["s3://...", "msg:..."]
  actorUserId: string; // who created it (D-1)
  actorRole: "household" | "clinician" | "admin";
  visibility: "household" | string; // default household (D-1)
  confidence?: "high" | "medium" | "low";
  needsReview?: boolean;
  deleted?: boolean; // soft-delete (D-41)
  deletedAt?: string;
}

/** A stored structured event. `payload` is type-specific (added per schema). */
export interface MedicalEvent extends AuditEnvelope {
  patientId: string;
  eventId: string;
  eventType: EventType;
  occurredAt: string; // ISO-8601 with timezone
  payload: Record<string, unknown>;
}

/**
 * Fields added to event payloads that have a large free-text notes field.
 * When `notes` exceeds NOTES_INLINE_THRESHOLD_BYTES, the full text is stored in
 * S3 (`events/{patientId}/{eventId}/notes.md`) and only a preview is kept inline
 * for list rendering. Consumers check `notesRef` to know whether to lazy-fetch.
 *
 * Applies to: visit, illness, surgery_or_procedure, hospitalization, vaccination.
 */
export interface LargeNotesFields {
  notes?: string;          // full text when inline (below threshold)
  notesPreview?: string;   // first ~280 chars when overflowed to S3
  notesRef?: string;       // S3 key when overflowed; absent when inline
}

/** Notes below this byte length stay fully inline; larger notes go to S3. */
export const NOTES_INLINE_THRESHOLD_BYTES = 4096;

/**
 * Per-patient avatar/accent colors (D-33). Curated so white initials stay legible
 * on every swatch. Shared so the picker (web) and validation (api) agree on the
 * allowed set; a new patient is auto-assigned the next color in order.
 */
export const PATIENT_COLORS = [
  "#2563eb",
  "#db2777",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#0891b2",
] as const;
export type PatientColor = (typeof PATIENT_COLORS)[number];

export interface Patient {
  patientId: string;
  firstName: string;
  lastName?: string;
  dob?: string;
  sex?: "female" | "male" | "intersex" | "unspecified";
  timezone: string; // IANA, e.g. "America/New_York"
  color: string; // per-Patient color (D-33)
  avatarInitial?: string;
  cycleTrackingEnabled: boolean;
  criticalWarnings: "enabled" | "muted" | "off"; // D-7
  /** Patient-specific local "HH:MM" times for day-part anchors, overriding the
   *  defaults (D-47). Used by the Regimens picker and the capture model so that
   *  "morning"/"dinner"/etc. resolve to this patient's actual routine. */
  dayPartTimes?: Partial<Record<ScheduledGroup, string>>;
  /** Approximate location for weather/AQI enrichment (Phase 3, §21.5). Stored in
   *  plain lat/lon (not precise street address). User-controlled opt-in. */
  locationLat?: number;
  locationLon?: number;
  locationCity?: string; // display label, e.g. "Boston, MA"
  /** When true, headache and symptom events are enriched with weather/AQI context
   *  from Open-Meteo at confirm time (§21.5). Requires locationLat/locationLon. */
  weatherEnrichmentEnabled?: boolean;
  /** ISO timestamp of the last time the patient confirmed their medication list
   *  was up-to-date. Used to drive the periodic medication-review reminder. */
  medicationReviewedAt?: string;
  /** Which drug-interaction severity levels to surface in the UI. Absent = DEFAULT_INTERACTION_ALERT_LEVELS. */
  interactionAlertLevels?: InteractionSeverity[];
  /** Whether medication side-effect-vs-symptom-history warnings show at capture
   *  time (MEDICATION_SIDE_EFFECT_WARNING_PLAN.md, D-SEW-2). Absent = true (on). */
  sideEffectAlertsEnabled?: boolean;
  /** Half-lives after the last dose at which a partner medication is considered cleared and no longer
   *  contributes to drug-interaction warnings. Absent = DEFAULT_INTERACTION_CLEARANCE_HALF_LIVES (5). */
  interactionClearanceHalfLives?: InteractionClearanceHalfLives;
  /** Dose-safety / overdose-guard settings (MEDICATION_OVERDOSE_PLAN.md). Absent = DEFAULT_DOSE_SAFETY_SETTINGS. */
  doseSafetySettings?: import("./doseSafety.js").DoseSafetySettings;
  /** Proactive-suggestion settings (PROACTIVE_SUGGESTIONS_PLAN.md). Absent = DEFAULT_PROACTIVE_SETTINGS. */
  proactiveSuggestions?: import("./proactiveSuggestions.js").ProactiveSuggestionSettings;
  /** Daily hydration goal for the Log Water page, stored canonically in mL so a
   *  display-unit change never corrupts the total. Absent = DEFAULT_WATER_GOAL_ML. */
  waterGoalMl?: number;
  /** Preferred display unit for fluids on the Log Water page. Absent = "oz". */
  preferredFluidUnit?: import("./hydration.js").FluidDisplayUnit;
  /** Preferred display/entry unit for height. The value stays canonical in
   *  heightCm; this only drives how it's shown and entered. Absent = "ft_in". */
  preferredHeightUnit?: import("./height.js").HeightDisplayUnit;
  /** Current body weight, stored canonically in kg. Updated here (not as a vital)
   *  so the UI can display/use it without scanning the event log. */
  weightKg?: number;
  /** Preferred display/entry unit for weight. The value stays canonical in
   *  weightKg; this only drives how it's shown and entered. Absent = "lbs". */
  preferredWeightUnit?: import("./weight.js").WeightDisplayUnit;
  /** Daily calorie goal for the Log Food page (FOOD_TRACKING_PLAN.md D-FOOD-8).
   *  Absent = suggested from the profile via suggestNutritionTargets(). */
  dailyCalorieGoal?: number;
  /** Target bowel movements per day for the Regularity page (D-BM-1). Absent = DEFAULT_BM_DAILY_GOAL (1). */
  bowelDailyGoal?: number;
  /** Days since last movement before the hero turns amber (D-BM-2). Absent = DEFAULT_BM_SOFT_DAYS (2). */
  bowelSoftConstipationDays?: number;
  /** Days since last movement before the hero turns red (D-BM-2). Absent = DEFAULT_BM_HARD_DAYS (3). */
  bowelHardConstipationDays?: number;
  /** Lateness tolerance (hours) for the medication Compliance card (MEDICATION_COMPLIANCE_PLAN.md
   *  D-MC-4). Clamped to [0, 24] server-side. Absent = DEFAULT_COMPLIANCE_TOLERANCE_HOURS (4). */
  complianceToleranceHours?: number;
  /** Weighted sessions per muscle per week that count as "fully trained" on the
   *  Balance body map (TRAINER_HOUSEHOLD_AND_LATERALITY_PLAN.md D-TGT-1). Absent =
   *  WEEKLY_TARGET_SESSIONS (2.0). Clamped [0.5, 7] server-side, not rounded. */
  weeklyMuscleTargetSessions?: number;
  /** Working sets per muscle per week that count as "at target" when the Balance
   *  map is in working-sets measure (D-TA-5). Absent = WEEKLY_SET_TARGET (10).
   *  Clamped [1, 60] server-side. Starting default, not a validated prescription. */
  weeklySetTarget?: number;
  /** Per-patient training goal that selects the sets/reps/load guidance variant shown in the
   *  Trainer log window (TRAINER_HEAT_RAMP_AND_EXERCISE_DOSING_PLAN.md D-DG-2). A per-patient
   *  preference because household members differ. Absent = DEFAULT_TRAINING_GOAL. Saved via
   *  POST /patients/preferences. */
  trainingGoal?: TrainingGoal;
  /** When true, Log recommendations may treat specialist muscles (neck, pelvic
   *  floor, foot, deep spinal stabilizers, rotator cuff) as gaps (D-IF-3).
   *  Absent = false. The body map still colours them either way. */
  includeSpecialistMuscles?: boolean;
  /** Daily macro targets in grams for the Log Food page (D-FOOD-11). Absent =
   *  derived from dailyCalorieGoal with the default 50/20/30 split. */
  macroTargets?: { carbsG?: number; proteinG?: number; fatG?: number };
  /** Standing medical-history profile — basic slice (D-63, §30). Slow-changing
   *  facts edited in place (not events), populated mainly via Onboarding mode and
   *  injected into Capture/Analysis prompts as a profile summary. */
  heightCm?: number;
  bloodType?: string;
  smokingStatus?: "never" | "former" | "current" | "unknown";
  alcoholUse?: string; // free text, e.g. "occasional", "none"
  activityLevel?: "sedentary" | "light" | "moderate" | "active";
  dietPattern?: string; // e.g. "vegetarian", "low-carb"
  primaryProvider?: string;
  pharmacy?: string;
  goals?: string; // what the patient hopes to figure out — seeds analysis
  profileUpdatedAt?: string;
  /** Cached list of medication allergy substance names (normalized lowercase).
   *  Maintained by confirmEvents when allergy events with category "medication"
   *  are saved. Avoids a full GSI1 scan on every chat turn (performance fix #7). */
  medicationAllergies?: string[];
  /** Per-patient dashboard layout (DASHBOARD_WIDGETS_PLAN.md D-DASH-W2). Ordered
   *  list of widget prefs; array order = display order. Absent = DEFAULT_DASHBOARD_LAYOUT. */
  dashboardLayout?: import("./dashboard.js").DashboardWidgetPref[];
  /** Preferred display/entry unit for body circumferences. Values stay canonical
   *  in centimeters; this only drives how they are shown and entered.
   *  Absent = DEFAULT_MEASUREMENT_UNIT ("in"). (D-MEAS-4) */
  preferredMeasurementUnit?: import("./bodyMeasurement.js").MeasurementDisplayUnit;
  /** User-defined measurement sites beyond the built-in catalog. Ids are always
   *  prefixed "custom/" so a future built-in can never collide (D-MEAS-16).
   *  Capped at MAX_CUSTOM_MEASUREMENT_SITES (8) server-side. */
  customMeasurementSites?: import("./bodyMeasurement.js").CustomMeasurementSite[];
  /** Days without a measuring session before the catch-up card appears.
   *  Absent = DEFAULT_MEASUREMENT_REMINDER_DAYS (30). Clamped [7, 180] server-side. */
  measurementReminderDays?: number;
  /** Whether logged health context (measurements, recent weight, training and
   *  nutrition summaries, fluid-retention medications) is sent to the body-photo
   *  vision model to calibrate its estimates. Absent = true. (D-MEAS-15) */
  bodyVisionContextEnabled?: boolean;
  createdAt: string;
  /** Set on a patient created by the full-fidelity importer (PATIENT_EXPORT_IMPORT_PLAN.md
   *  D3/D6). `importedFromExportId` is the source envelope's `exportId` and drives the
   *  re-run guard; `importSourcePatientId` is the original partition id (informational). */
  importedFromExportId?: string;
  importedAt?: string;
  importSourcePatientId?: string;
}

/** Weather snapshot attached to a headache or symptom event (§21.5). */
export interface WeatherContext {
  temperatureC: number;
  humidity: number; // 0-100
  pressureHpa: number;
  weatherCode?: number; // WMO weather code
  measuredAt: string; // ISO-8601 of the closest hourly reading
}

/** Air quality snapshot attached to a headache or symptom event (§21.5). */
export interface AirQualityContext {
  usAqi?: number;
  pm25?: number; // μg/m³
  dominantPollutant?: string;
  measuredAt: string;
}

/** Profile fields settable via Onboarding mode's set_patient_profile tool (D-63). */
export const PATIENT_PROFILE_FIELDS = [
  "dob",
  "sex",
  "timezone",
  "heightCm",
  "bloodType",
  "smokingStatus",
  "alcoholUse",
  "activityLevel",
  "dietPattern",
  "primaryProvider",
  "pharmacy",
  "goals",
] as const;
export type PatientProfileField = (typeof PATIENT_PROFILE_FIELDS)[number];

// ---- Medication interaction types (MEDICATION_INTERACTIONS_PLAN.md §3, §5) ----

export const INTERACTION_SEVERITIES = [
  "contraindicated",
  "do_not_use",
  "serious",
  "moderate",
  "minor",
] as const;
export type InteractionSeverity = (typeof INTERACTION_SEVERITIES)[number];

/** Default alert levels shown to the user — conservative but not overwhelming. */
export const DEFAULT_INTERACTION_ALERT_LEVELS: InteractionSeverity[] = [
  "contraindicated",
  "do_not_use",
  "serious",
  "moderate",
];

export interface InteractionWarning {
  severity: InteractionSeverity;
  drug1: string;
  drug2: string;
  description: string;
  rxcui1?: string;
  rxcui2?: string;
  /**
   * Short noun-phrase effects a patient might notice from this interaction
   * (e.g. "drowsiness", "increased bleeding risk"), ordered common→rare.
   * Optional: legacy cache entries and the "none" verdict carry no symptoms.
   * Aggregated across active drugs by `aggregateInteractionSymptoms`.
   */
  symptoms?: string[];
}

/**
 * Returns the alert levels configured for a patient, or the application
 * default when the patient has not specified a preference. Mirrors the
 * `resolveDayPartTimes()` pattern so server and UI always agree on the default.
 */
export function resolveInteractionAlertLevels(
  levels?: InteractionSeverity[]
): InteractionSeverity[] {
  return levels && levels.length > 0 ? levels : [...DEFAULT_INTERACTION_ALERT_LEVELS];
}

/**
 * Selectable thresholds for how long a previous medication is considered
 * "still present" when evaluating drug–drug interaction warnings. A higher
 * number of half-lives means the clearance window is wider — warnings persist
 * longer after the last dose (more conservative).
 *
 * Fraction remaining = 0.5 ^ halfLives:
 *   4 ≈ 6.3%  |  5 ≈ 3.1% (default)  |  6 ≈ 1.6%  |  7 ≈ 0.78%
 */
export const INTERACTION_CLEARANCE_OPTIONS = [
  { halfLives: 4, pct: "~6%",  label: "4 half-lives (~6% remaining) — relaxed" },
  { halfLives: 5, pct: "~3%",  label: "5 half-lives (~3% remaining) — standard" },
  { halfLives: 6, pct: "~2%",  label: "6 half-lives (~2% remaining) — conservative" },
  { halfLives: 7, pct: "~1%",  label: "7 half-lives (~1% remaining) — strict" },
] as const;

export type InteractionClearanceHalfLives =
  (typeof INTERACTION_CLEARANCE_OPTIONS)[number]["halfLives"];

export const INTERACTION_CLEARANCE_HALF_LIFE_VALUES = INTERACTION_CLEARANCE_OPTIONS.map(
  (o) => o.halfLives
) as readonly InteractionClearanceHalfLives[];

/** 5 half-lives ≈ 3.1% remaining — the standard pharmacokinetic "effectively cleared" cutoff. */
export const DEFAULT_INTERACTION_CLEARANCE_HALF_LIVES: InteractionClearanceHalfLives = 5;

/** Returns the configured clearance threshold (in half-lives), or the default. */
export function resolveInteractionClearanceHalfLives(
  halfLives?: number
): InteractionClearanceHalfLives {
  return (INTERACTION_CLEARANCE_HALF_LIFE_VALUES as readonly number[]).includes(halfLives ?? -1)
    ? (halfLives as InteractionClearanceHalfLives)
    : DEFAULT_INTERACTION_CLEARANCE_HALF_LIVES;
}

/** A single chat message persisted to the session (PLAN.md §7). */
export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  seq: number;
  createdAt: string;
  modelId?: string;
  /** CHAT_DEPTH_MODES_PLAN.md: which depth produced/asked this turn. Absent = quick (legacy rows). */
  depth?: ChatDepth;
  /** Event citations resolved for a Deep / Second-opinion answer (eventId + display label). */
  citations?: Array<{ eventId: string; label: string }>;
  /** One-sentence first-person summary Deep offered to log (D-CD-4); the UI shows a "Log this" button. */
  logOffer?: string;
  /** On a Second-opinion assistant message: the seq of the Deep answer it re-examines. */
  secondOpinionOfSeq?: number;
}

/** A chat session's metadata (PLAN.md §7, §20.3). */
export interface ChatSession {
  sessionId: string;
  patientId: string;
  title: string;
  startedAt: string;
  lastMessageAt: string;
  /** Which mode this session belongs to (D-58). Absent = "capture" (legacy rows). */
  mode?: AppMode;
}

/** Streaming chunk protocol sent from the chat Lambda to the SPA. */
export type ChatStreamChunk =
  | { type: "text"; delta: string }
  | { type: "done"; messageId: string }
  | { type: "error"; message: string };
